import React, { useCallback, useMemo } from 'react';

import { useTranslate } from 'gatsby-theme-soluble-source';

import { graphql } from 'gatsby';

import useModal from '../../hooks/useModal';

import CarouselSection, {
    CarouselSectionProps,
} from 'design-system/src/components/organisms/CarouselSection';

import { getImage, getSrc } from '../../utils/media';

interface Carousel1SectionCMSProps {
    items: any[];
    title: string;
    titleTag: CarouselSectionProps['titleTag'];
    description?: string;
}

export default function Carousel1Section({
    items,
    title,
    titleTag,
}: Carousel1SectionCMSProps) {
    const _ = useTranslate();

    const { openModal } = useModal();

    const onClick = useCallback(
        (data: any) => {
            if (!data) {
                return;
            }

            openModal(true, {
                videoId: data?.video?.videoId,
                cta: data?.modalCta ?? data?.cta,
                description: data?.modalDescription,
            });
        },
        [openModal],
    );

    const thisItems = useMemo(() => {
        if (!items || !items.length) {
            return [];
        }

        return items.map((item) => {
            return {
                ...item,
                person:
                    item?.kind === 'external'
                        ? {
                              name: item?.name,
                              position: item?.role,
                              linkedin: item?.linkedin,
                              g2: item?.g2,
                          }
                        : {
                              name: item?.author?.name,
                              position: item?.author?.role,
                              linkedin: item?.author?.linkedin,
                              g2: item?.author?.g2,
                          },
                media:
                    item?.kind === 'external'
                        ? getImage(item?.media)
                        : getImage(item?.author?.image),
                logo: {
                    alt: item?.logo?.alt,
                    src: getSrc(item?.logo),
                },
                onClickPlay: () => onClick(item),
                hasVideo: !!item?.video?.videoId,
                cta: {
                    label: _['webinar.ondemand.cta'].text,
                    href: item?.cta?.href,
                    target: '_blank',
                    onClick: (ev: React.MouseEvent) => {
                        ev?.stopPropagation();
                    },
                },
            };
        });
    }, [_, items, onClick]);

    return (
        <CarouselSection
            title={title}
            titleTag={titleTag}
            items={thisItems}
            variant="testimonial"
        />
    );
}

export const query = graphql`
    fragment DatoCmsCarousel1Section on DatoCmsCarousel1Section {
        title
        titleTag
        items {
            ...TestimonialItem
        }
    }

    fragment TestimonialItem on DatoCmsTestimonial {
        text: testimonial
        role: job
        title
        titleTag
        kind
        name
        author {
            name
            role
            linkedin
            g2
            image {
                ...SourceMedia
                gatsbyImageData(
                    placeholder: BLURRED
                    width: 1024
                    height: 1024
                    imgixParams: {
                        w: "1024"
                        h: "1024"
                        fit: "crop"
                        crop: "faces"
                    }
                )
            }
        }
        logo {
            ...SourceMedia
            gatsbyImageData(
                placeholder: BLURRED
                imgixParams: { fit: "max", h: "72", auto: "format" }
            )
        }
        media {
            ...SourceMedia
            gatsbyImageData(
                placeholder: BLURRED
                width: 1250
                imgixParams: { w: "1250", fit: "max" }
            )
        }
        video {
            ...YoutubeVideo
        }
        cta {
            ...Button
        }
        modalCta {
            ...Button
        }
        modalDescription
        linkedin
    }
`;
